import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useJourneyDraft, useJourneyPreview, useJourneyPublish, useJourneyHistory } from "@/lib/journeyQueries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { JourneyRail } from "../../JourneyRail";
import JourneyVersionModal from "../JourneyVersionModal";

/**
 * Master v5 Prompt 17 — Journey Configuration. Every preview here
 * renders through the EXACT same _build_rail_nodes real clients see
 * (journey_config_repository.py) — never a locally-faked rail.
 */
export default function JourneyConfigTab({ workspaceId, memberId }) {
  const { data: draft, isPending, isError } = useJourneyDraft(workspaceId);
  const { data: history } = useJourneyHistory(workspaceId);
  const previewMutation = useJourneyPreview(workspaceId);
  const publishMutation = useJourneyPublish(workspaceId);
  const [stages, setStages] = useState([]);
  const [openVersion, setOpenVersion] = useState(null);

  useEffect(() => {
    if (draft) setStages(draft.stage_specs.map((s) => ({ ...s })));
  }, [draft]);

  function update(idx, field, value) {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  }
  function move(idx, dir) {
    setStages((prev) => {
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next.map((s, i) => ({ ...s, order_index: i }));
    });
  }
  function remove(idx) {
    setStages((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order_index: i })));
  }
  function addStage() {
    setStages((prev) => [...prev, { stage_key: `stage_${prev.length + 1}`, label: "New stage", layer_key: draft.approved_layer_keys[0], order_index: prev.length, is_hidden: false, track: "member", completion_policy: "manual", is_review_milestone: false }]);
  }

  async function handlePreview() {
    try {
      await previewMutation.mutateAsync({ stageSpecs: stages, targetMembershipId: memberId });
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't preview this Journey");
    }
  }
  async function handlePublish() {
    try {
      const result = await publishMutation.mutateAsync(stages);
      toast.success(`Published as version ${result.version_number}`);
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't publish this Journey");
    }
  }

  if (isPending || !draft) return <p className="text-sm text-ink-soft" data-testid="journey-config-loading">Loading journey…</p>;
  if (isError) return <p className="text-sm text-red-600" data-testid="journey-config-error">Couldn't load journey configuration.</p>;

  return (
    <div data-testid="client-360-journey-config-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-lg text-ink">Stages ({draft.source === "current_version" ? `from v${draft.version_number}` : "from template"})</h2>
          <button onClick={addStage} className="text-xs flex items-center gap-1 text-terracotta" data-testid="journey-add-stage-button"><Plus size={12} /> Add stage</button>
        </div>
        <div className="space-y-2">
          {stages.map((s, i) => (
            <div key={i} className="border border-ink/10 rounded-lg p-3 bg-white/50 space-y-2" data-testid={`journey-stage-${s.stage_key}`}>
              <div className="flex items-center gap-2">
                <Input value={s.label} onChange={(e) => update(i, "label", e.target.value)} className="h-8 text-sm" data-testid={`journey-stage-label-${s.stage_key}`} />
                <button onClick={() => update(i, "is_hidden", !s.is_hidden)} className="text-ink-soft" data-testid={`journey-stage-toggle-hidden-${s.stage_key}`}>{s.is_hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-ink-soft disabled:opacity-30" data-testid={`journey-stage-move-up-${s.stage_key}`}><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} disabled={i === stages.length - 1} className="text-ink-soft disabled:opacity-30" data-testid={`journey-stage-move-down-${s.stage_key}`}><ArrowDown size={13} /></button>
                <button onClick={() => remove(i)} className="text-ink-soft hover:text-red-600" data-testid={`journey-stage-remove-${s.stage_key}`}><Trash2 size={13} /></button>
              </div>
              <div className="flex items-center gap-2">
                <Select value={s.layer_key} onValueChange={(v) => update(i, "layer_key", v)}>
                  <SelectTrigger className="h-7 text-xs w-40" data-testid={`journey-stage-layer-key-${s.stage_key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{draft.approved_layer_keys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={s.track} onValueChange={(v) => update(i, "track", v)}>
                  <SelectTrigger className="h-7 text-xs w-28" data-testid={`journey-stage-track-${s.stage_key}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="shared">Shared</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={handlePreview} disabled={previewMutation.isPending} className="text-sm font-medium border border-ink/15 rounded-full px-4 py-1.5 text-ink hover:bg-ink/5 transition-colors disabled:opacity-50" data-testid="journey-preview-button">
            {previewMutation.isPending ? "Previewing…" : "Preview client Home rail"}
          </button>
          <button onClick={handlePublish} disabled={publishMutation.isPending} className="text-sm font-medium bg-terracotta text-paper px-4 py-1.5 rounded-full hover:bg-terracotta/90 transition-colors disabled:opacity-50" data-testid="journey-publish-button">
            {publishMutation.isPending ? "Publishing…" : "Publish new version"}
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-lg text-ink mb-3">Exact client Home rail preview</h2>
        {previewMutation.data ? (
          <div className="border border-ink/10 bg-white/40 rounded-xl p-4" data-testid="journey-preview-rail">
            <JourneyRail nodes={previewMutation.data.nodes} testIdPrefix="journey-preview" />
          </div>
        ) : (
          <p className="text-sm text-ink-soft" data-testid="journey-preview-empty">Click "Preview client Home rail" to see exactly what this client would see.</p>
        )}

        <h2 className="font-serif text-lg text-ink mt-6 mb-2">Version history</h2>
        <div className="space-y-1.5" data-testid="journey-history-list">
          {(history?.items || []).map((v) => (
            <div key={v.version_number} className="text-xs text-ink-soft border border-ink/10 rounded-md px-2.5 py-1.5 bg-white/50 flex items-center justify-between" data-testid={`journey-history-item-${v.version_number}`}>
              <span>v{v.version_number} · {v.stage_count} stages · published {new Date(v.published_at).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo" })}</span>
              <button onClick={() => setOpenVersion(v.version_number)} className="text-terracotta hover:underline shrink-0" data-testid={`journey-history-open-${v.version_number}`}>Open →</button>
            </div>
          ))}
        </div>
      </div>
      {openVersion && <JourneyVersionModal workspaceId={workspaceId} versionNumber={openVersion} memberId={memberId} onClose={() => setOpenVersion(null)} />}
    </div>
  );
}
